import { Box, Tooltip, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

export type BadgeType = 'status' | 'criticality' | 'organization-status' | 'user-role' | 'user-status' | 'cost-total' | 'qs_status';

interface UniversalBadgeProps {
  type: BadgeType;
  value: string | boolean;
  onClick?: () => void;
  canModify?: boolean;
  isUpdating?: boolean;
  'data-testid'?: string;
}

// Zentralisierte Übersetzungsfunktionen
const getDisplayText = (type: BadgeType, value: string | boolean, t: ReturnType<typeof useTranslation>['t']): string => {
  if (typeof value === 'boolean') {
    // Kritikalität
    return value ? t('dashboard:csv.critical') : t('dashboard:csv.simple');
  }

  const stringValue = String(value).toLowerCase().trim();

  switch (type) {

    case 'criticality':
      switch (stringValue) {
        case 'kritisch': return t('dashboard:csv.critical');
        case 'einfach': return t('dashboard:csv.simple');
        case 'nicht kritisch': return t('dashboard:csv.simple');
        case 'true': return t('dashboard:csv.critical');
        case 'false': return t('dashboard:csv.simple');
        default: return t('dashboard:csv.simple');
      }
    case 'status':
      switch (stringValue) {
        case 'open': return t('common:status.open');
        case 'pending': return t('common:status.pending');
        case 'analyzing': return t('common:status.analyzing');
        case 'complete': return t('common:status.complete');
        case 'failed': return t('common:status.failed');
        case 'empty': return t('common:status.empty');
        case 'incomplete': return t('common:status.incomplete');
        case 'qs_processing': return t('common:status.qsInProgress');
        case 'inreview': return t('common:status.analyzing');
        case 'waiting': return t('dashboard:status.pending');
        // Deutsche Übersetzungen
        case 'offen': return t('common:status.open');
        case 'ausstehend': return t('common:status.pending');
        case 'wartend': return t('common:status.pending');
        case 'in analyse': return t('common:status.analyzing');
        case 'abgeschlossen': return t('common:status.complete');
        case 'fehlgeschlagen': return t('common:status.failed');
        // QS-Status spezifisch behandeln - alle als "QS in Bearbeitung"
        case 'qs_pending': return t('common:status.qsInProgress');
        case 'qs_in_review': return t('common:status.qsInProgress');
        case 'qs_completed': return t('common:status.qsInProgress');
        case 'qs_approved': return t('common:status.qsInProgress');
        case 'qs_rejected': return t('common:status.qsInProgress');
        default:
          // Fallback für andere QS-Status
          if (stringValue.startsWith('qs_')) {
            return t('common:status.qsInProgress');
          }
          return value as string;
      }
    case 'qs_status':
      switch (stringValue) {
        case 'complete': return t('common:status.complete');
        // QS-Status spezifisch behandeln - alle als "QS in Bearbeitung"
        case 'qs_pending': return t('common:status.pending');
        case 'qs_in_review': return t('common:status.qsInProgress');
        case 'qs_completed': return t('common:status.complete');
        case 'qs_approved': return t('common:status.complete');
        case 'qs_rejected': return t('common:status.failed');
        default: return value as string;
      }
    case 'organization-status':
      switch (stringValue) {
        case 'active': return 'Aktiv';
        case 'inactive': return 'Inaktiv';
        default: return 'Unbekannt';
      }

    case 'user-role':
      switch (stringValue) {
        case 'admin': return 'Administrator';
        case 'user': return 'Benutzer';
        case 'reader': return 'Benutzer';
        case 'qs': return 'QS';
        case 'super_qs': return 'Super QS';
        case 'superqs': return 'Super QS';
        default: return value as string || 'Unbekannt';
      }

    case 'user-status':
      switch (stringValue) {
        case 'active': return 'Aktiv';
        case 'inactive': return 'Inaktiv';
        case 'pending': return 'Wartend';
        case 'app_access_removed': return 'App-Zugriff entfernt';
        case 'deleted': return 'Gelöscht';
        default: return 'Unbekannt';
      }

    case 'cost-total':
      // Für Kostenbadges wird der Wert direkt angezeigt
      return value as string;

    default:
      return value as string;
  }
};

// Zentralisierte Farbfunktionen - mit Dark Mode Unterstützung
const getBackgroundColor = (type: BadgeType, value: string | boolean, isDark: boolean): string => {
  if (typeof value === 'boolean') {
    // Kritikalität - Neutraler Hintergrund
    return isDark ? '#374151' : '#ffffff';
  }

  const stringValue = String(value).toLowerCase().trim();

  switch (type) {
    case 'status':
      switch (stringValue) {
        case 'complete': return isDark ? '#1b5e20' : '#c8e6c9';
        case 'analyzing': return isDark ? '#e65100' : '#ffe0b2';
        case 'open': return isDark ? '#4a4a4a' : '#e0e0e0';
        case 'pending': return isDark ? '#0d47a1' : '#bbdefb';
        case 'failed': return isDark ? '#b71c1c' : '#ffcdd2';
        case 'empty': return isDark ? '#4a4a4a' : '#e0e0e0';
        case 'incomplete': return isDark ? '#4a4a4a' : '#e0e0e0';
        case 'qs_processing': return isDark ? '#e65100' : '#ffe0b2';
        case 'inreview': return isDark ? '#e65100' : '#ffe0b2';
        case 'waiting': return isDark ? '#0d47a1' : '#bbdefb';
        // Deutsche Übersetzungen
        case 'offen': return isDark ? '#4a4a4a' : '#e0e0e0';
        case 'ausstehend': return isDark ? '#0d47a1' : '#bbdefb';
        case 'wartend': return isDark ? '#0d47a1' : '#bbdefb';
        case 'in analyse': return isDark ? '#e65100' : '#ffe0b2';
        case 'abgeschlossen': return isDark ? '#1b5e20' : '#c8e6c9';
        case 'fehlgeschlagen': return isDark ? '#b71c1c' : '#ffcdd2';
        // QS-Status
        case 'qs_pending': return isDark ? '#e65100' : '#ffe0b2';
        case 'qs_in_review': return isDark ? '#e65100' : '#ffe0b2';
        case 'qs_completed': return isDark ? '#e65100' : '#ffe0b2';
        case 'qs_approved': return isDark ? '#e65100' : '#ffe0b2';
        case 'qs_rejected': return isDark ? '#e65100' : '#ffe0b2';
        default:
          if (stringValue.startsWith('qs_')) {
            return isDark ? '#e65100' : '#ffe0b2';
          }
          return isDark ? '#4a4a4a' : '#e0e0e0';
      }
    case 'qs_status':
      switch (stringValue) {
        case 'complete': return isDark ? '#1b5e20' : '#c8e6c9';
        case 'qs_pending': return isDark ? '#4a4a4a' : '#e0e0e0';
        case 'qs_in_review': return isDark ? '#e65100' : '#ffe0b2';
        case 'qs_completed': return isDark ? '#0d47a1' : '#bbdefb';
        case 'qs_approved': return isDark ? '#1b5e20' : '#c8e6c9';
        case 'qs_rejected': return isDark ? '#b71c1c' : '#ffcdd2';
        default: return isDark ? '#4a4a4a' : '#e0e0e0';
      }
    case 'organization-status':
      switch (stringValue) {
        case 'active': return isDark ? '#1b5e20' : '#c8e6c9';
        case 'inactive': return isDark ? '#e65100' : '#ffe0b2';
        default: return isDark ? '#4a4a4a' : '#e0e0e0';
      }

    case 'user-role':
      switch (stringValue) {
        case 'admin': return isDark ? '#0d47a1' : '#bbdefb';
        case 'user':
        case 'reader': return isDark ? '#4a4a4a' : '#e0e0e0';
        case 'qs': return isDark ? '#b45309' : '#fff9c4';
        case 'super_qs':
        case 'superqs': return isDark ? '#4a148c' : '#e1bee7';
        default: return isDark ? '#4a4a4a' : '#e0e0e0';
      }

    case 'user-status':
      switch (stringValue) {
        case 'active': return isDark ? '#1b5e20' : '#c8e6c9';
        case 'inactive': return isDark ? '#4a4a4a' : '#e0e0e0';
        case 'pending': return isDark ? '#b45309' : '#fff3cd';
        case 'app_access_removed': return isDark ? '#e65100' : '#ffe0b2';
        case 'deleted': return isDark ? '#b71c1c' : '#ffcdd2';
        default: return isDark ? '#4a4a4a' : '#e0e0e0';
      }

    case 'cost-total':
      return isDark ? '#166534' : '#dcfce7';

    default:
      return isDark ? '#4a4a4a' : '#e0e0e0';
  }
};

const getTextColor = (type: BadgeType, value: string | boolean, isDark: boolean): string => {
  if (typeof value === 'boolean') {
    // Kritikalität - Textfarbe
    return isDark ? '#e5e7eb' : '#374151';
  }

  const stringValue = String(value).toLowerCase().trim();

  switch (type) {
    case 'status':
      switch (stringValue) {
        case 'complete': return isDark ? '#bbf7d0' : '#1b5e20';
        case 'analyzing': return isDark ? '#fed7aa' : '#e65100';
        case 'open': return isDark ? '#e5e7eb' : '#424242';
        case 'pending': return isDark ? '#bfdbfe' : '#0d47a1';
        case 'failed': return isDark ? '#fecaca' : '#b71c1c';
        case 'empty': return isDark ? '#e5e7eb' : '#424242';
        case 'incomplete': return isDark ? '#e5e7eb' : '#424242';
        case 'qs_processing': return isDark ? '#fed7aa' : '#e65100';
        case 'inreview': return isDark ? '#fed7aa' : '#e65100';
        case 'waiting': return isDark ? '#bfdbfe' : '#0d47a1';
        // Deutsche Übersetzungen
        case 'offen': return isDark ? '#e5e7eb' : '#424242';
        case 'ausstehend': return isDark ? '#bfdbfe' : '#0d47a1';
        case 'wartend': return isDark ? '#bfdbfe' : '#0d47a1';
        case 'in analyse': return isDark ? '#fed7aa' : '#e65100';
        case 'abgeschlossen': return isDark ? '#bbf7d0' : '#1b5e20';
        case 'fehlgeschlagen': return isDark ? '#fecaca' : '#b71c1c';
        // QS-Status
        case 'qs_pending': return isDark ? '#fed7aa' : '#e65100';
        case 'qs_in_review': return isDark ? '#fed7aa' : '#e65100';
        case 'qs_completed': return isDark ? '#fed7aa' : '#e65100';
        case 'qs_approved': return isDark ? '#fed7aa' : '#e65100';
        case 'qs_rejected': return isDark ? '#fed7aa' : '#e65100';
        default:
          if (stringValue.startsWith('qs_')) {
            return isDark ? '#fed7aa' : '#e65100';
          }
          return isDark ? '#e5e7eb' : '#424242';
      }
    case 'qs_status':
      switch (stringValue) {
        case 'complete': return isDark ? '#bbf7d0' : '#1b5e20';
        case 'qs_pending': return isDark ? '#e5e7eb' : '#424242';
        case 'qs_in_review': return isDark ? '#fed7aa' : '#e65100';
        case 'qs_completed': return isDark ? '#bfdbfe' : '#0d47a1';
        case 'qs_approved': return isDark ? '#bbf7d0' : '#1b5e20';
        case 'qs_rejected': return isDark ? '#fecaca' : '#b71c1c';
        default: return isDark ? '#e5e7eb' : '#424242';
      }
    case 'organization-status':
      switch (stringValue) {
        case 'active': return isDark ? '#bbf7d0' : '#1b5e20';
        case 'inactive': return isDark ? '#fed7aa' : '#e65100';
        default: return isDark ? '#e5e7eb' : '#424242';
      }

    case 'user-role':
      switch (stringValue) {
        case 'admin': return isDark ? '#bfdbfe' : '#0d47a1';
        case 'user':
        case 'reader': return isDark ? '#e5e7eb' : '#424242';
        case 'qs': return isDark ? '#fef3c7' : '#b45309';
        case 'super_qs':
        case 'superqs': return isDark ? '#f3e8ff' : '#4a148c';
        default: return isDark ? '#e5e7eb' : '#424242';
      }

    case 'user-status':
      switch (stringValue) {
        case 'active': return isDark ? '#bbf7d0' : '#1b5e20';
        case 'inactive': return isDark ? '#e5e7eb' : '#424242';
        case 'pending': return isDark ? '#fef3c7' : '#b45309';
        case 'app_access_removed': return isDark ? '#fed7aa' : '#e65100';
        case 'deleted': return isDark ? '#fecaca' : '#b71c1c';
        default: return isDark ? '#e5e7eb' : '#424242';
      }

    case 'cost-total':
      return isDark ? '#bbf7d0' : '#166534';

    default:
      return isDark ? '#e5e7eb' : '#424242';
  }
};

const getMinWidth = (type: BadgeType): string => {
  switch (type) {
    case 'criticality': return '80px';
    case 'status': return '100px';
    case 'organization-status': return '60px';
    case 'user-role': return '70px';
    case 'user-status': return '80px';
    case 'cost-total': return '120px'; // Breiter für Kostenangaben
    default: return '80px';
  }
};

const UniversalBadge: React.FC<UniversalBadgeProps> = ({
  type,
  value,
  onClick,
  canModify = false,
  isUpdating = false,
  'data-testid': dataTestId,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const displayText = getDisplayText(type, value, t);
  const backgroundColor = getBackgroundColor(type, value, isDark);
  const textColor = getTextColor(type, value, isDark);
  const minWidth = getMinWidth(type);

  const isClickable = onClick && canModify;
  const hoverColor = type === 'criticality' && typeof value === 'boolean'
    ? (isDark ? '#4b5563' : '#f3f4f6')
    : undefined;

  const badge = (
    <Box
      onClick={isClickable ? onClick : undefined}
      data-testid={dataTestId}
      sx={{
        bgcolor: backgroundColor,
        color: textColor,
        py: type === 'cost-total' ? 1 : 0.75,
        px: 2,
        borderRadius: type === 'cost-total' ? '6px' : 1,
        border: type === 'cost-total' ? `1px solid ${isDark ? '#166534' : '#bbf7d0'}` :
          type === 'criticality' ? `1px solid ${isDark ? '#6b7280' : '#d1d5db'}` : 'none',
        display: 'inline-block',
        fontSize: type === 'cost-total' ? '1.125rem' : '0.875rem',
        fontWeight: type === 'cost-total' ? 700 : 500,
        cursor: isClickable ? 'pointer' : 'default',
        opacity: !canModify || isUpdating ? 0.6 : 1,
        transition: 'opacity 0.2s, background-color 0.2s, border-color 0.2s',
        '&:hover': {
          bgcolor: isClickable ? hoverColor : undefined,
          borderColor: type === 'criticality' && isClickable ? (isDark ? '#9ca3af' : '#9ca3af') : undefined,
        },
        minWidth,
        textAlign: 'center',
      }}>
      {displayText}
    </Box>
  );

  if (isClickable && type === 'criticality') {
    return (
      <Tooltip title={t('providers:serviceDetail.changeCriticality')} arrow>
        {badge}
      </Tooltip>
    );
  }

  return badge;
};

export default UniversalBadge; 